import {
  Chart,
  ChartEvent,
  ChartType,
  Plugin,
} from "chart.js";
import { AxisLabelEventOptions } from "./axis-label-event.options";
import { isNumber, isString } from 'lodash-es';
import { TooltipComponent } from '../../../simple-mat-tooltip';

class AxisLabelEventPlugin<T extends ChartType = ChartType, O = Record<string, unknown>> implements Plugin<T, O> {
    id: string = 'axisLabelEventPlugin';

    private isNumberRegexp: RegExp = /^[+-]?([0-9]*[.])?[0-9]+$/;

    private numberFormatter =  Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })

    private defaultConfig: AxisLabelEventOptions = {
      onLabelMouseMove: (label: string, event: ChartEvent, chart: Chart) => {},
      xPostfix: '',
      yPostfix: '',
    };

    beforeEvent(chart: Chart, args: { event: ChartEvent, replay: boolean, cancelable: true, inChartArea: boolean }, options: O): boolean | void {
      const { scales: {x, y} } = chart;
      const event = args.event;
      const opt: AxisLabelEventOptions ={ ...this.defaultConfig, ...options };

      const postfix = {
        x: opt.xPostfix || '',
        y: opt.yPostfix || '',
      }

      let label = null;

      if (event.type === 'mousemove') {
        const xAxisIndex: number = x.getValueForPixel(event.x);
        const yAxisIndex: number = y.getValueForPixel(event.y);
        const indexAxis: 'x' | 'y' = chart.options?.indexAxis || 'x';
        const secondaryAxis: 'x' | 'y' = indexAxis == 'x' ? 'y' : 'x';
        const maxLabelValue: number = chart.scales[indexAxis].max;
        const maxIndexValue: number = chart.scales[secondaryAxis].max;
        const chartAreaTop: number = chart.scales.y.top;
        const chartAreaLeft: number = chart.scales.y.left;

        if (args.inChartArea) {
          const hoverOptions = chart.options.hover;
          const intersections = chart.getElementsAtEventForMode(event.native, hoverOptions.mode, hoverOptions, false);
          if (intersections.length > 0) {
            const index: number = indexAxis == 'x' ? xAxisIndex : yAxisIndex;
            const dataLabel: string | string[] = <string | string[]>chart.data.labels[index];
            const dataValue: number = <number>chart.data.datasets[0].data[index];
            label = `${(Array.isArray(dataLabel) ? dataLabel.join(' ') : dataLabel) + postfix[indexAxis]}: ${this.formatString(dataValue, postfix[secondaryAxis])}`;
          }
        } else {
          if (event.x > chartAreaLeft && event.y > chartAreaTop) {
            if (xAxisIndex >= 0) {
              if (indexAxis == 'x') {
                if (xAxisIndex <= maxLabelValue) {
                  label = this.formatString(x.getLabelForValue(xAxisIndex), postfix.x);
                }
              } else {
                if (xAxisIndex <= maxIndexValue) {
                  label = this.formatString(x.getLabelForValue(xAxisIndex), postfix.x);
                }
              }
            } else if (yAxisIndex >= 0) {
              if (indexAxis == 'y') {
                if (yAxisIndex <= maxLabelValue) {
                  label = this.formatString(y.getLabelForValue(yAxisIndex), postfix.y);
                }
              } else {
                if (yAxisIndex <= maxIndexValue) {
                  label = this.formatString(y.getLabelForValue(yAxisIndex), postfix.y);
                }
              }
            }
          }
        }

        opt.onLabelMouseMove(label, event, chart);
      } else if (event.type === 'mouseout') {
        opt.onLabelMouseMove(label, event, chart);
      }
    }

    private formatString(value: any, postfix: string): string {
      if (isString(value)) {
        let cleanStr = value.replace(',', '');
        return (this.isNumberRegexp.test(cleanStr) ? this.numberFormatter.format(parseFloat(cleanStr)) : value) + postfix;
      } else if (isNumber(value)) {
        return this.numberFormatter.format(value) + postfix;
      } else if (Array.isArray(value)) {
        return value.join(' ') + postfix;
      }
      return value + postfix;
    }
}

export const axisLabelEventPlugin: Plugin = new AxisLabelEventPlugin();

export function defaultOnLabelMouseMove(tooltip: TooltipComponent): (label: string, event: ChartEvent) => void  {
  return (label: string, event: ChartEvent) => {
    if (label) {
      requestAnimationFrame(() => {
        let { x, y } = <any>event.native;
        tooltip.message = label;
        tooltip.position = { x: x + 5 , y };
        tooltip.show = true;
        tooltip.update();
      });
    } else if (tooltip.show) {
      tooltip.show = false;
      tooltip.update();
    }
  }
}
